/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { type Permission } from 'application/Shared/types/permission';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useEffect, useState } from 'react';

interface FetchPermissionsReturns {
    /** loading state */
    loading: boolean;
    /** error if request failed */
    error: unknown | null;
    /** permissions data */
    permissions: Permission[] | null;
}

interface PermissionsParams {
    defaultNewUser?: boolean;
    includeClients?: boolean;
    platformTenantId?: string;
    size?: number;
}

/**
 * A custom React hook that fetches permissions data from the server.
 *
 * @param {Permission} params - The parameters for fetching permissions
 *
 * @returns {Object} An object containing:
 *   @property {boolean} loading - Indicates if the permissions are currently being fetched
 *   @property {unknown | null} error - Error object if the request failed, null otherwise
 *   @property {Permission[] | null} permissions - Array of permission objects if successful, null otherwise
 *
 * @example
 * const { loading, error, permissions } = useFetchPermissions({
 *   platformTenantId: 'tenant123',
 *   defaultNewUser: false,
 *   includeClients: true
 * });
 *
 * if (loading) {
 *   return <LoadingSpinner />;
 * }
 *
 * if (error) {
 *   return <Error message="Failed to load permissions" />;
 * }
 *
 * if (!permissions) {
 *   return <Empty message="No permissions found" />;
 * }
 *
 * @throws Will log an error if the API request fails or returns no data
 * @note The hook automatically handles cleanup of in-flight requests when the component unmounts
 */
export const useFetchPermissions = ({
    defaultNewUser,
    includeClients,
    platformTenantId,
    size
}: PermissionsParams): FetchPermissionsReturns => {
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown | null>(null);

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            try {
                const response = await axios.get('/permission', {
                    cancelToken: source.token,
                    params: {
                        defaultNewUser,
                        includeClients,
                        platformTenantId,
                        size
                    }
                });
                if (!Response.hasData(response)) {
                    debug.e('Unable to load permissions. No data.');
                } else {
                    setPermissions(Response.getData(response));
                }
            } catch (error) {
                debug.e('Unable to load permissions.', error);
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [defaultNewUser, includeClients, platformTenantId, size]);

    return {
        error,
        loading,
        permissions
    };
};
